/**
 * The class page for students and trainers:  lobby -> live room -> class summary.
 *
 * Everything that changes while a class runs (people, hands, chat, polls, whether it is live) arrives in
 * ONE poll, POST /classes/:id/heartbeat every few seconds. Media (camera, microphone, screen) goes through
 * the video adapter, not through this server.
 */
import { config } from '../../core/config.js';
import { api, saveDownload } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { debounce, fmtDateTime, hue, initials } from '../../core/format.js';
import { currentView, every, reload, update } from '../../app/shell.js';
import { av, backLink, banner, bar, ic, seg, statusTag, tag } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { attachButton, fileChip, uploadChosen } from '../../ui/upload.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { createVideo } from './video.js';

const BEAT_MS = 3000;   // default check-in; the server asks for a slower one in a big room (poll_ms)
let lastBeat = 0;
let video = null;
let joinedSession = null;

const roomUi = () => ({
  joined: false, joining: false, tab: 'chat', mic: false, cam: false, share: false, hand: false, muted: false, camOff: false,
  messages: [], people: [], hands: [], poll: null, sinceId: 0, liveStatus: null, error: '', videoMode: 'none', draft: '',
  notesShared: '', notesVer: '', notesMine: '', noteState: '',
});

/* ---------- notes ---------- */

function setNoteState(text) {
  const view = currentView();
  if (view?.ui) view.ui.noteState = text;
  const el = document.getElementById('note-state');
  if (el) el.textContent = text;
}

async function saveNote(kind, body) {
  const view = currentView();
  if (!view?.data?.class) return;
  try {
    const { note } = await api.put(`/classes/${view.data.class.id}/notes/${kind}`, { body: { body } });
    if (kind === 'shared') view.ui.notesVer = note.version; // our own save: the heartbeat need not send it back
    setNoteState('Saved');
  } catch (error) {
    setNoteState('Could not save. Check your connection; your text is still here.');
    if (error.status === 422) toast(error.message, 'alert');
  }
}

const saveSharedNote = debounce(() => saveNote('shared', currentView()?.ui?.notesShared ?? ''), 1200);
const saveMyNote = debounce(() => saveNote('mine', currentView()?.ui?.notesMine ?? ''), 1200);

/* ---------- data flow ---------- */

async function beat() {
  const view = currentView();
  if (!view || !view.ui || view.ui.joined === undefined) return;
  const id = view.data.class.id;
  lastBeat = Date.now();
  const r = await api.post(`/classes/${id}/heartbeat`, { body: { since_id: view.ui.sinceId, notes_ver: view.ui.notesVer || null, people_ver: view.ui.peopleVer || null }, signal: view.ctx.signal });
  update((ui, d) => {
    ui.liveStatus = r.status;
    if (r.people) ui.people = r.people;   // null = same as last time
    ui.peopleVer = r.people_ver || null;
    ui.pollMs = r.poll_ms || BEAT_MS;
    ui.hands = r.hands;
    ui.poll = r.poll;
    ui.hand = r.me.hand_raised;
    if (r.notes) {
      if (r.notes.body != null && d.class.role !== 'trainer') ui.notesShared = r.notes.body; // the trainer is the author: never overwrite what they are typing
      if (d.class.role !== 'trainer' || r.notes.body == null) ui.notesVer = r.notes.version;
    }
    ui.muted = r.me.muted;
    ui.camOff = r.me.camera_off;
    if (r.me.muted && ui.mic) {
      ui.mic = false;
      video?.setMic(false).catch(() => {});
    }
    if (r.me.camera_off && ui.cam) {
      ui.cam = false;
      video?.setCamera(false).catch(() => {});
    }
    if (r.messages.length) {
      const seen = new Set(ui.messages.map((m) => m.id));
      for (const m of r.messages) if (!seen.has(m.id)) ui.messages.push(m);
      ui.sinceId = ui.messages.at(-1).id;
    }
    d.class.status = r.status;
  });
  if (r.status === 'ended' || r.status === 'cancelled') await finish();
}

async function finish() {
  video?.disconnect();
  video = null;
  joinedSession = null;
  await reload();
  update((ui) => { ui.joined = false; });
}

async function enterRoom() {
  const view = currentView();
  const { class: c } = view.data;
  update((ui) => { ui.joining = true; ui.error = ''; });
  try {
    const info = await api.post(`/classes/${c.id}/join`);
    video = await createVideo(info.video);
    joinedSession = c.id;
    update((ui) => { ui.joined = true; ui.joining = false; ui.muted = info.muted_by_trainer; ui.camOff = !!info.camera_off_by_trainer; ui.videoMode = video.mode; });
    const stage = document.getElementById('lk-stage');
    if (stage) {
      try {
        await video.connect(stage, { onChange: () => update() });
        if (c.role === 'trainer') {
          await video.setMic(true);
          await video.setCamera(true);
          update((ui) => { ui.mic = true; ui.cam = true; });
        }
      } catch (error) {
        update((ui) => { ui.error = 'Video could not connect. You can still use chat. Try rejoining.'; });
        console.error(error);
      }
    }
    try {
      const n = await api.get(`/classes/${c.id}/notes`);
      update((ui) => { ui.notesShared = n.shared.body; ui.notesVer = n.shared.version; ui.notesMine = n.mine?.body || ''; });
    } catch { /* notes are optional: the class still runs */ }
    await beat();
  } catch (error) {
    update((ui) => { ui.joining = false; ui.error = error.message; });
  }
}

/* ---------- views ---------- */

function summary(c, role) {
  return html`<div class="card"><div class="row between">${statusTag(c.state === 'live' ? 'live' : c.status)}<span class="xs mu">${fmtDateTime(c.starts_at)}</span></div>
    <h2 class="mt-s">${c.course}, batch ${c.batch}</h2><p class="mu">Class ${c.number}${c.title && c.title !== `Class ${c.number}` ? `: ${c.title}` : ''}${c.trainer ? `. Trainer ${c.trainer}` : ''}. ${c.duration_min} minutes.</p>
    <div class="list sm">${config.recordings ? html`<div class="li">${ic('play')} This class is recorded</div>` : ''}
      ${c.students != null ? html`<div class="li">${ic('users')} ${c.students} student${c.students === 1 ? '' : 's'} in this batch</div>` : ''}
      <div class="li">${ic('file')} ${c.materials.length} ${c.materials.length === 1 ? 'material' : 'materials'} attached</div></div>
    ${c.materials.length ? html`<div class="list mt-s">${c.materials.map((m) => html`<div class="li">${ic('file')}<span class="f1 sm">${m.title}</span><button type="button" class="btn soft sm" data-act="file.open" data-id="${m.file.id}">${ic('dl')} Open</button></div>`)}</div>` : ''}
    ${role === 'student' ? html`<div class="row mt-s"><button type="button" class="btn ghost sm" data-act="class.reminder" data-on="${!c.reminder}">${ic('cal')} ${c.reminder ? 'Remove reminder' : 'Remind me'}</button>
      <button type="button" class="btn ghost sm" data-act="class.ics" data-id="${c.id}">${ic('dl')} Add to calendar</button></div>` : ''}</div>`;
}

function lobby(c, ui, role) {
  const trainer = role === 'trainer';
  const live = c.status === 'live';
  let cta;
  if (trainer && !live) {
    cta = html`<button type="button" class="btn lg wide" data-act="room.start" ${c.can_join ? '' : 'disabled'}>Start class</button>`;
  } else {
    cta = html`<button type="button" class="btn lg wide" data-act="room.join" ${c.can_join && !ui.joining ? '' : 'disabled'}>${ui.joining ? 'Joining' : live ? 'Join class' : 'Join the lobby'}</button>`;
  }
  return html`<div class="grid g2" style="align-items:start">${summary(c, role)}
    <div class="card">${c.can_join ? html`<span class="live">${live ? 'Live now' : 'Room is open'}</span>` : tag('Not open yet', 'n')}
      <h3 class="mt-s">${live ? 'The class is running' : trainer ? 'Start when you are ready' : 'Waiting for the trainer'}</h3>
      <p class="mu">${c.can_join ? (trainer ? 'Students are told the moment you start.' : 'You will be marked present when you join a live class.') : c.join_blocked_reason}</p>
      ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}${cta}
      <p class="note mt-s">Use headphones to avoid echo. Your browser will ask to use your microphone and camera.</p></div></div>`;
}

function tiles(ui) {
  if (ui.videoMode === 'livekit') {
    return html`<div class="stage"><div id="lk-stage" class="vstage" data-morph="skip"></div></div>`;
  }
  return html`<div class="stage"><div class="tile main"><div class="col" style="align-items:center;text-align:center">${ic('camoff')}<span>Video is not connected in this environment.</span><span class="xs">Chat, hands and polls still work.</span></div></div>
    <div class="tiles">${ui.people.slice(0, 8).map((p) => html`<div class="tile"><div class="bigav" style="background:hsl(${hue(p.name)} 40% 35%)">${initials(p.name)}</div><span class="nm">${ic(p.muted ? 'micoff' : 'mic')} ${p.name}${p.hand_raised ? ' ✋' : ''}</span></div>`)}</div></div>`;
}

function pollCard(ui, role) {
  const p = ui.poll;
  if (!p || (p.status === 'closed' && role === 'student' && p.my_vote == null)) return '';
  const trainer = role === 'trainer';
  const showResults = Boolean(p.results);
  return html`<div class="card flat" style="margin:10px"><div class="row between"><b class="sm">${p.question}</b>${p.status === 'closed' ? tag('Closed', 'n') : tag('Open', 'ok')}</div>
    <div class="mt-s">${p.options.map((o, i) => (showResults
      ? html`<div class="mb"><div class="row between sm"><span>${o}${p.my_vote === i ? html` <span class="tag ok">Your answer</span>` : ''}${p.correct_index === i ? html` <span class="tag">Correct</span>` : ''}</div><b>${p.results.percents[i]}%</b></div>${bar(p.results.percents[i], p.correct_index === i ? 'ok' : '', o)}</div>`
      : html`<button type="button" class="btn ghost sm wide mb" data-act="room.vote" data-poll="${p.id}" data-option="${i}">${o}</button>`))}</div>
    ${trainer && p.status === 'open' ? html`<button type="button" class="btn ghost sm" data-act="room.poll.close" data-poll="${p.id}">Close poll</button>` : ''}</div>`;
}

function notesPanel(ui, role) {
  const state = html`<span class="xs mu" id="note-state" aria-live="polite">${ui.noteState}</span>`;
  if (role === 'trainer') {
    return html`<div style="padding:12px 14px;display:flex;flex-direction:column;gap:8px">
      <label class="sm" for="note-shared"><b>Class notes</b> <span class="mu">Everyone in the class sees these as you type.</span></label>
      <textarea id="note-shared" rows="12" maxlength="20000" placeholder="Write the points for today's class" data-input="room.note.shared">${ui.notesShared}</textarea>${state}</div>`;
  }
  return html`<div style="padding:12px 14px;display:flex;flex-direction:column;gap:8px;overflow:auto;max-height:520px">
    <div><b class="sm">Trainer's notes</b>
      <div class="card flat mt-s" style="white-space:pre-wrap;overflow-wrap:anywhere;min-height:60px">${ui.notesShared || html`<span class="mu sm">Nothing yet. The trainer's notes appear here.</span>`}</div></div>
    <label class="sm" for="note-mine"><b>My notes</b> <span class="mu">Only you can see these.</span></label>
    <textarea id="note-mine" rows="8" maxlength="20000" placeholder="Write what you want to remember" data-input="room.note.mine">${ui.notesMine}</textarea>${state}</div>`;
}

function sidePanel(ui, role) {
  const trainer = role === 'trainer';
  const body = ui.tab === 'notes' ? notesPanel(ui, role) : ui.tab === 'people'
    ? html`<div class="list" style="padding:4px 14px;overflow:auto;max-height:420px">${ui.people.map((p) => html`<div class="li">${av(p.name, 'sm')}<span class="f1 sm">${p.name}${p.role === 'trainer' ? html` <span class="tag">Trainer</span>` : ''}${p.hand_raised ? ' ✋' : ''}</span>
        ${trainer && p.role === 'student' ? html`<button type="button" class="btn ghost sm" data-act="room.mute" data-kind="mic" data-user="${p.user_id}" data-muted="${!p.muted}">${p.muted ? 'Allow mic' : 'Mute mic'}</button>
          <button type="button" class="btn ghost sm" data-act="room.mute" data-kind="camera" data-user="${p.user_id}" data-muted="${!p.camera_off}">${p.camera_off ? 'Allow camera' : 'Camera off'}</button>
          <button type="button" class="btn ghost sm" data-act="room.remove" data-user="${p.user_id}" data-name="${p.name}" aria-label="Remove ${p.name}">${ic('x')}</button>` : ic(p.muted ? 'micoff' : 'mic')}</div>`)}
      ${ui.people.length ? '' : html`<p class="mu sm">Nobody else is here yet.</p>`}</div>`
    : html`<div class="msgs" id="room-msgs" data-stick-bottom style="max-height:340px">${ui.messages.map((m) => (m.kind === 'system'
        ? html`<div class="m sys">${m.body}</div>`
        : html`<div class="m ${m.role === role || (m.role === 'professional' && trainer) ? 'me' : ''}" data-key="m${m.id}"><span class="w">${m.name}${m.role === 'professional' ? ' (Trainer)' : ''}</span>${m.body}${fileChip(m.file)}</div>`))}</div>
      <div class="compose">${trainer ? attachButton('room.attach') : ''}<input id="room-in" placeholder="Message the class" aria-label="Message the class" maxlength="1000" value="${ui.draft}" data-input="room.draft" data-enter="room.send"><button type="button" class="btn" data-act="room.send" aria-label="Send">${ic('send')}</button></div>`;
  return html`<div class="card" style="padding:0;display:flex;flex-direction:column;min-height:420px">
    <div style="padding:10px 12px;border-bottom:1px solid var(--ln)">${seg([['chat', 'Chat'], ['notes', 'Notes'], ['people', `People (${ui.people.length})`]], ui.tab, 'room.tab', 'Room panel')}</div>${pollCard(ui, role)}${body}</div>`;
}

function live(c, ui, role) {
  const trainer = role === 'trainer';
  return html`<div class="row between mb"><div><span class="live">Live</span> <b>${c.course}, batch ${c.batch}</b> <span class="mu sm">Class ${c.number}</span></div><span class="sm mu">${ic('users')} ${ui.people.length} in class</span></div>
    ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}${ui.muted && !trainer ? banner('The trainer has muted your microphone. Your camera is not affected.', '', 'info') : ''}${ui.camOff && !trainer ? banner('The trainer has switched your camera off.', '', 'info') : ''}
    <div class="room"><div>${tiles(ui)}
      <div class="ctrls" role="toolbar" aria-label="Class controls">
        <button type="button" class="cb ${ui.mic ? '' : 'off'}" data-act="room.mic" ${ui.muted && !trainer ? 'disabled' : ''} aria-pressed="${ui.mic}">${ic(ui.mic ? 'mic' : 'micoff')}${ui.mic ? 'Mute' : 'Unmute'}</button>
        <button type="button" class="cb ${ui.cam ? '' : 'off'}" data-act="room.cam" ${ui.camOff && !trainer ? 'disabled' : ''} aria-pressed="${ui.cam}">${ic(ui.cam ? 'cam' : 'camoff')}${ui.cam ? 'Stop video' : 'Start video'}</button>
        ${trainer ? html`<button type="button" class="cb ${ui.share ? 'act' : ''}" data-act="room.share" aria-pressed="${ui.share}">${ic('screen')}${ui.share ? 'Stop share' : 'Share screen'}</button>
          <button type="button" class="cb" data-act="room.poll.new">${ic('poll')}Launch poll</button>`
          : html`<button type="button" class="cb ${ui.hand ? 'act' : ''}" data-act="room.hand" aria-pressed="${ui.hand}">${ic('hand')}${ui.hand ? 'Lower hand' : 'Raise hand'}</button>`}
        <button type="button" class="cb end" data-act="${trainer ? 'room.end' : 'room.leave'}">${ic('out')}${trainer ? 'End class' : 'Leave'}</button></div>
      ${trainer && ui.hands.length ? banner(`${ui.hands.length} ${ui.hands.length === 1 ? 'student has' : 'students have'} a hand raised. Open People to unmute.`, '', 'hand') : ''}</div>
      ${sidePanel(ui, role)}</div>`;
}

function ended(c, role, d) {
  const recording = c.recording_id;
  return html`<div class="card" style="max-width:700px"><h2>${c.status === 'cancelled' ? 'This class was cancelled' : 'Class completed'}</h2><p class="mu">${c.course}, batch ${c.batch}. Class ${c.number}${c.title && c.title !== `Class ${c.number}` ? `: ${c.title}` : ''}.</p>
    ${role === 'student' && c.status === 'ended' ? html`<div class="grid stats">${tag(c.attended ? 'Present' : 'Absent', c.attended ? 'ok' : 'wn')}</div>` : ''}
    <div class="list mt">${recording ? html`<div class="li">${ic('play')}<span class="f1">Recording available</span><a class="btn soft sm" href="/${role === 'trainer' ? 'professional' : 'student'}/recordings/${recording}">Watch</a></div>` : c.status === 'ended' && config.recordings ? html`<div class="li">${ic('play')}<span class="f1">Recording is being processed. We will notify you.</span></div>` : ''}
      ${role === 'student' && c.status === 'ended' ? html`<div class="li">${ic('star')}<span class="f1">How was this class?</span><span class="row" style="gap:2px">${[1, 2, 3, 4, 5].map((n) => html`<button type="button" class="btn icon" style="color:${(c.my_rating || 0) >= n ? 'var(--mgb)' : 'var(--mu)'}" aria-label="${n} stars" data-act="class.rate" data-stars="${n}">${ic('star')}</button>`)}</span></div>` : ''}</div>
    ${c.status === 'ended' && d.notes ? html`<h3 class="mt">Class notes</h3>
      <div class="card flat" style="white-space:pre-wrap;overflow-wrap:anywhere">${d.notes.shared.body || html`<span class="mu sm">The trainer did not leave notes for this class.</span>`}</div>
      ${role === 'student' ? html`<label class="sm mt-s" for="note-mine-end" style="display:block"><b>My notes</b> <span class="mu">Only you can see these. They save when you click away.</span></label>
        <textarea id="note-mine-end" rows="6" maxlength="20000" data-change="room.note.mine.end">${d.notes.mine?.body || ''}</textarea>` : ''}` : ''}
    ${role === 'trainer' && d.attendance ? attendanceCard(d) : ''}</div>`;
}

function attendanceCard(d) {
  return html`<h3 class="mt">Attendance</h3><div class="list">${d.attendance.map((a) => html`<label class="li" style="cursor:pointer"><input type="checkbox" ${a.present ? 'checked' : ''} data-change="class.attend" data-user="${a.user_id}"><span class="f1 sm">${a.name}</span>${a.source === 'auto' ? html`<span class="xs mu">auto</span>` : ''}</label>`)}</div>`;
}

/* ---------- routes ---------- */

function classRoute(role) {
  const area = role === 'trainer' ? 'professional' : 'student';
  return {
    path: `/${area}/classes/:id`,
    roles: [area],
    nav: role === 'trainer' ? 'coaching' : 'classes',
    title: (d) => (d?.class ? `${d.class.course}, class ${d.class.number}` : 'Class'),
    ui: roomUi,
    async load(ctx) {
      const { class: c } = await api.get(`/classes/${ctx.params.id}`, { signal: ctx.signal });
      const out = { class: c };
      if (role === 'trainer' && c.status === 'ended') out.attendance = (await api.get(`/classes/${c.id}/attendance`, { signal: ctx.signal })).attendance;
      if (c.status === 'ended') out.notes = await api.get(`/classes/${c.id}/notes`, { signal: ctx.signal }).catch(() => null);
      return out;
    },
    enter() {
      every(1000, () => {
        const v = currentView();
        const c = v?.data?.class;
        if (!c || (!v.ui.joined && !(c.status === 'live' || c.status === 'scheduled'))) return undefined;
        if (!v.ui.joined && !c.can_join && c.status !== 'live') return undefined;
        if (Date.now() - lastBeat < (v.ui.pollMs || BEAT_MS) - 200) return undefined;
        return beat();
      });
    },
    leave() {
      if (joinedSession) api.post(`/classes/${joinedSession}/leave`).catch(() => {});
      video?.disconnect();
      video = null;
      joinedSession = null;
    },
    view(d, ui) {
      const c = d.class;
      const back = backLink(role === 'trainer' ? '/professional/coaching' : '/student/classes', role === 'trainer' ? 'Coaching' : 'Live classes');
      if (ui.joined) return live(c, ui, role);
      if (c.status === 'ended' || c.status === 'cancelled') return html`${back}${ended(c, role, d)}`;
      return html`${back}${lobby(c, ui, role)}`;
    },
  };
}

/* ---------- actions ---------- */

const thisClass = () => currentView().data.class;

register({
  'room.join': () => enterRoom(),
  'room.start': async () => {
    await api.post(`/classes/${thisClass().id}/start`);
    thisClass().status = 'live';
    await enterRoom();
  },
  'room.leave': async () => {
    const sure = await confirmBox({ title: 'Leave the class?', text: 'Your attendance is already marked. You can rejoin while the class is live.', label: 'Leave class', danger: true, cancel: 'Stay' });
    if (!sure) return;
    await api.post(`/classes/${thisClass().id}/leave`).catch(() => {});
    video?.disconnect();
    video = null;
    joinedSession = null;
    update((ui) => { ui.joined = false; });
  },
  'room.end': async () => {
    const sure = await confirmBox({ title: 'End class for everyone?', text: config.recordings ? 'The recording will be shared with the batch and the class counts toward your earnings.' : 'The class counts toward your earnings. This class is not being recorded.', label: 'End class', danger: true, cancel: 'Keep going' });
    if (!sure) return;
    await api.post(`/classes/${thisClass().id}/end`);
    await finish();
  },
  'room.mic': async () => {
    const next = !currentView().ui.mic;
    await video?.setMic(next);
    update((ui) => { ui.mic = next; });
  },
  'room.cam': async () => {
    const next = !currentView().ui.cam;
    await video?.setCamera(next);
    update((ui) => { ui.cam = next; });
  },
  'room.share': async () => {
    const next = !currentView().ui.share;
    try {
      await video?.setShare(next);
      update((ui) => { ui.share = next; });
    } catch {
      toast('Screen sharing did not start. If your browser asked for permission, choose a screen or window and press Share. If nothing appeared, allow screen recording for your browser in the computer settings.', 'alert');
    }
  },
  'room.hand': async () => {
    const raised = !currentView().ui.hand;
    await api.post(`/classes/${thisClass().id}/hand`, { body: { raised } });
    update((ui) => { ui.hand = raised; });
    if (raised) toast('Hand raised. Your trainer can see it.', 'hand');
  },
  'room.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),
  'room.draft': (el) => { currentView().ui.draft = el.value; },
  'room.note.shared': (el) => { currentView().ui.notesShared = el.value; setNoteState('Saving…'); saveSharedNote(); },
  'room.note.mine': (el) => { currentView().ui.notesMine = el.value; setNoteState('Saving…'); saveMyNote(); },
  'room.note.mine.end': async (el) => {
    try {
      await api.put(`/classes/${thisClass().id}/notes/mine`, { body: { body: el.value } });
      toast('Notes saved', 'check');
    } catch (error) {
      toast(error.message, 'alert');
    }
  },
  'room.attach': async (el) => {
    try {
      const stored = await uploadChosen(el, 'chat');
      if (!stored) return;
      const input = document.getElementById('room-in');
      const { message } = await api.post(`/classes/${thisClass().id}/chat`, { body: { body: (input?.value || '').trim(), file_id: stored.id } });
      update((ui) => {
        if (!ui.messages.some((m) => m.id === message.id)) ui.messages.push(message);
        ui.sinceId = Math.max(ui.sinceId, message.id);
        ui.draft = '';
      });
      if (input) input.value = '';
      toast('Document shared with the class', 'check');
    } catch (error) {
      toast(error.message, 'alert');
    } finally {
      el.value = '';
    }
  },
  'room.send': async () => {
    const input = document.getElementById('room-in');
    const body = (input?.value || '').trim();
    if (!body) return;
    try {
      const { message } = await api.post(`/classes/${thisClass().id}/chat`, { body: { body } });
      update((ui) => {
        if (!ui.messages.some((m) => m.id === message.id)) ui.messages.push(message);
        ui.sinceId = Math.max(ui.sinceId, message.id);
        ui.draft = '';
      });
      input.value = '';
    } catch (error) {
      if (error.status === 422 || error.status === 409) toast(error.message, 'alert');
      else throw error;
    }
  },
  'room.vote': async (el) => {
    await api.post(`/classes/${thisClass().id}/polls/${el.dataset.poll}/vote`, { body: { option: Number(el.dataset.option) } });
    await beat();
  },
  'room.mute': async (el) => {
    await api.post(`/classes/${thisClass().id}/people/${el.dataset.user}/mute`, { body: { muted: el.dataset.muted === 'true', kind: el.dataset.kind || 'mic' } });
    await beat();
  },
  'room.remove': async (el) => {
    const sure = await confirmBox({ title: `Remove ${el.dataset.name}?`, text: 'They are taken out of the room. They can rejoin unless you report the behaviour.', label: 'Remove', danger: true });
    if (!sure) return;
    await api.post(`/classes/${thisClass().id}/people/${el.dataset.user}/remove`);
    await beat();
  },
  'room.poll.new': () => openModal({
    title: 'Launch a poll',
    body: html`<form id="poll-form" data-submit="room.poll.create" novalidate>${field('poll-q', 'Question', { name: 'question', maxlength: 300 })}
      ${field('poll-o', 'Options, one per line (2 to 6)', { name: 'options', rows: 4, max: 700 })}
      ${field('poll-c', 'Number of the correct option (optional)', { type: 'number', name: 'correct', req: false, min: 1, max: 6 })}</form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="poll-form" class="btn">Send poll</button>`,
  }),
  'room.poll.create': (form) => submitForm(form, async (values) => {
    const options = values.options.split('\n').map((x) => x.trim()).filter(Boolean);
    const body = { question: values.question, options };
    if (values.correct) body.correct_index = Number(values.correct) - 1;
    await api.post(`/classes/${thisClass().id}/polls`, { body });
    closeModal(false);
    toast('Poll sent', 'poll');
    await beat();
  }),
  'room.poll.close': async (el) => {
    await api.post(`/classes/${thisClass().id}/polls/${el.dataset.poll}/close`);
    await beat();
  },
  'class.rate': async (el) => {
    await api.post(`/classes/${thisClass().id}/rating`, { body: { stars: Number(el.dataset.stars) } });
    thisClass().my_rating = Number(el.dataset.stars);
    update();
    toast('Thanks for rating the class', 'star');
  },
  'class.reminder': async (el) => {
    const on = el.dataset.on === 'true';
    await api.put(`/classes/${thisClass().id}/reminder`, { body: { on } });
    thisClass().reminder = on;
    update();
    toast(on ? 'Reminder set' : 'Reminder removed', 'bell');
  },
  'class.ics': async (el) => {
    await saveDownload(`/classes/${el.dataset.id}/calendar.ics`, `class-${el.dataset.id}.ics`);
  },
  'class.attend': async (el) => {
    await api.put(`/classes/${thisClass().id}/attendance/${el.dataset.user}`, { body: { present: el.checked } });
    toast('Attendance saved');
  },
});

export const studentClassRoute = classRoute('student');
export const trainerClassRoute = classRoute('trainer');
